// Card 26, hazards on ridges (R548, ui-map-interface.md §17.3.26).
//
// INT 1 gives a rock awash a cross with four dots, a drying rock an
// asterisk, and a reef a dotted danger line. Here the danger line runs just
// outside a ridge's rim, which is the limit a mission line refuses to cross;
// the crosses sit on the rim cells a fleet's path clips; and the star marks a
// single rock cell, the easiest thing on the map to run a torpedo into.
//
// **THE DANGER LINE IS OUTSIDE THE BODY, SO THE PLANE CANNOT DRAW IT.** The
// terrain plane carries an INSIDE distance and reads zero everywhere outside
// a body (`render/terrain-features.ts`). The line is therefore the traced
// hex outline pushed out by `line_offset` along its own vertex normals
// (`render/offset-loop.ts`), which is one pure function and no field at all.
//
// **THE MARKS ARE THE SHARED GLYPHS.** The cross, the four dots and the star
// come from `render/chart-glyphs.ts`, so the KEYS legend draws the very same
// mark through `render/canvas-sink.ts` and the two cannot drift.
//
// Terrain never changes inside a world, so the whole piece is built one time
// per hello and costs one flush a frame after that.

import { CHART } from '../../../gen/presentation';
import { chart as S } from '../../../gen/strings';
import { crossDots, star } from '../../../render/chart-glyphs';
import { offsetLoop } from '../../../render/offset-loop';
import { COLS, ROWS, cellCenter, neighborAt } from '../../../world';
import type { Loop } from '../../../render/contours';
import type { ChartContext, ChartSinks, Piece } from '../context';
import { CHART_INK, dashedLoop, faceOf } from './shared';

/** The figures the piece reads out of its own table. */
const T = CHART.pieces.hazards;

/** One cross on a rim cell, already pushed in along its own normal. */
interface Cross { x: number; y: number }

/** One single rock cell: the star, the historical dots and the label. */
interface Rock { x: number; y: number }

export class HazardsPiece implements Piece {
  readonly id = 'hazards' as const;
  readonly tier = 'sea' as const;
  readonly pack = 'static' as const;
  /** The rock the fight happened round. It stays under a replay film. */
  readonly film = 'keep' as const;
  readonly rebuildOn = ['hello', 'reading', 'zoomband'] as const;

  private lines: Loop[] = [];
  private crosses: Cross[] = [];
  private rocks: Rock[] = [];

  visible(ctx: ChartContext): boolean { return ctx.terrain !== null; }

  rebuild(ctx: ChartContext): void {
    this.lines = [];
    this.crosses = [];
    this.rocks = [];
    const terrain = ctx.terrain;
    if (!terrain) return;
    const blocked = ctx.scene.blocked;
    for (const f of terrain.ridges) {
      if (f.singleRock) {
        this.rocks.push({ x: f.centroid.x, y: f.centroid.y });
        continue;
      }
      for (const loop of f.outline) {
        if (loop.length >= 3) this.lines.push(offsetLoop(loop, T.fig.line_offset));
      }
      // Every `cross_every`-th rim cell, in ascending cell index, so the
      // marks are a function of the mask alone.
      for (let i = 0; i < f.rimCells.length; i += T.fig.cross_every) {
        const cell = f.rimCells[i];
        const [cx, cy] = cellCenter(cell);
        const [ix, iy] = inward(cell, cx, cy, blocked);
        this.crosses.push({
          x: cx + ix * T.fig.cross_inset,
          y: cy + iy * T.fig.cross_inset,
        });
      }
    }
  }

  emit(sinks: ChartSinks, ctx: ChartContext): void {
    const style = ctx.cfg.pieces.hazards[ctx.reading];
    const modern = ctx.reading === 'modern';
    const face = faceOf(ctx.reading);
    for (const loop of this.lines) {
      dashedLoop(sinks.segments, loop, style.width, CHART_INK.pale, T.fig.line_alpha, style.dash);
    }
    for (const c of this.crosses) {
      crossDots(
        sinks.segments, sinks.circles, c.x, c.y, T.fig.cross_r,
        CHART_INK.pale, style.alpha, style.width, modern,
      );
    }
    const label = modern ? S.rock_modern : S.rock_historical;
    for (const r of this.rocks) {
      if (modern) {
        star(sinks.segments, r.x, r.y, T.fig.star_r, CHART_INK.pale, style.alpha, style.width);
      } else {
        crossDots(
          sinks.segments, sinks.circles, r.x, r.y, T.fig.cross_r,
          CHART_INK.pale, style.alpha, style.width, false,
        );
        const dots = T.historical.arr.dots;
        for (let i = 0; i + 1 < dots.length; i += 2) {
          sinks.circles.disc(
            r.x + dots[i], r.y + dots[i + 1], T.historical.fig.dot_r,
            CHART_INK.pale, style.alpha,
          );
        }
      }
      ctx.glyphs.word(
        sinks.quads, label, r.x + T.fig.rk_dx, r.y, face, T.text.main,
        CHART_INK.pale, T.fig.label_alpha,
      );
    }
  }

  /** What the last rebuild found. The test reads all three. */
  get counts(): { lines: number; crosses: number; rocks: number } {
    return {
      lines: this.lines.length,
      crosses: this.crosses.length,
      rocks: this.rocks.length,
    };
  }
}

/**
 * The unit vector from a rim cell toward the body it belongs to.
 *
 * It is the sum of the directions to the cell's WATER neighbours, negated,
 * so the cross is pushed away from the open sea and into the rock. The water
 * outside the board counts, exactly as `rimCells` counts it. A cell the sum
 * cancels on — a neck with water on two opposite sides — answers straight
 * up, so the caller never divides by zero.
 */
function inward(
  cell: number, cx: number, cy: number, blocked: Uint8Array | null,
): [number, number] {
  let sx = 0, sy = 0;
  for (let k = 0; k < 6; k++) {
    const nb = neighborAt(cell, k, COLS, ROWS);
    if (nb >= 0 && blocked && blocked[nb] !== 0) continue;
    const [nx, ny] = nb >= 0 ? cellCenter(nb) : offBoard(k, cx, cy);
    sx += nx - cx; sy += ny - cy;
  }
  const len = Math.hypot(sx, sy);
  if (len < 1e-6) return [0, -1];
  return [-sx / len, -sy / len];
}

/**
 * Where the neighbour would be if the board went on.
 *
 * A rim cell on the edge of the map has water beyond it, and the cross must
 * be pushed away from that water too. The direction of a hex step is one
 * sixth of a turn from due right, which is what `cellCenter` lays the six
 * neighbours out on.
 */
function offBoard(k: number, cx: number, cy: number): [number, number] {
  const a = (Math.PI / 3) * k;
  return [cx + Math.cos(a), cy + Math.sin(a)];
}

/** The one instance the registry holds. */
export const hazards = new HazardsPiece();
